import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {setSessionExpiredTrigger} from '@/lib/api/auth';
import {setUserSessionExpiredTrigger} from '@/lib/api/core';

export type SessionType = 'user' | 'customer';

interface SessionExpiredContextValue {
  isModalOpen: boolean;
  sessionType: SessionType | null;
  showSessionExpiredModal: (type: SessionType) => void;
  hideSessionExpiredModal: () => void;
}

const SessionExpiredContext = createContext<SessionExpiredContextValue | null>(null);

export function useSessionExpired() {
  const context = useContext(SessionExpiredContext);
  if (!context) {
    throw new Error('useSessionExpired must be used within SessionExpiredProvider');
  }
  return context;
}

interface SessionExpiredProviderProps {
  children: React.ReactNode;
}

export function SessionExpiredProvider({ children }: SessionExpiredProviderProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sessionType, setSessionType] = useState<SessionType | null>(null);

  const showSessionExpiredModal = useCallback((type: SessionType) => {
    setSessionType(type);
    setIsModalOpen(true);
  }, []);

  const hideSessionExpiredModal = useCallback(() => {
    setIsModalOpen(false);
    setSessionType(null);
  }, []);

  // Create wrappers for different session types to match the old API
  const showCustomerSessionExpiredModal = useCallback(() => {
    showSessionExpiredModal('customer');
  }, [showSessionExpiredModal]);

  const showUserSessionExpiredModal = useCallback(() => {
    showSessionExpiredModal('user');
  }, [showSessionExpiredModal]);

  // Register the trigger functions with their respective modules
  useEffect(() => {
    setSessionExpiredTrigger(showCustomerSessionExpiredModal);
    setUserSessionExpiredTrigger(showUserSessionExpiredModal);

    // Clean up on unmount
    return () => {
      setSessionExpiredTrigger(null);
      setUserSessionExpiredTrigger(null);
    };
  }, [showCustomerSessionExpiredModal, showUserSessionExpiredModal]);

  const value: SessionExpiredContextValue = {
    isModalOpen,
    sessionType,
    showSessionExpiredModal,
    hideSessionExpiredModal,
  };

  return (
    <SessionExpiredContext.Provider value={value}>
      {children}
    </SessionExpiredContext.Provider>
  );
}
